import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import CircularProgress from '@mui/material/CircularProgress';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

import { getContract, getContractPdf, getContractCategories, getReceiptDownload } from '../../../api/contract';
import {
  DetailContainer,
  MemberBar,
  MemberInfoGroup,
  MemberName,
  MemberBadge,
  MemberDivider,
  MemberText,
  MemberDetailButton,
  ActionButtonGroup,
  PdfDownloadWrap,
  PdfDownloadButton,
  DownloadMenu,
  DownloadMenuInner,
  DownloadMenuItem,
  BackToListButton,
  ContentRow,
  LeftPanel,
  RightPanel,
  ContractDocWrapper,
  ReceiptCard,
  ReceiptTitle,
  ReceiptEmpty,
  ReceiptEmptyText,
  ReceiptImage,
  ReceiptPdfViewer,
  SourceInfoRow,
  SourceInfoLabel,
  SourceInfoValue,
} from './ElectronicContractDetailStyled';


const ElectronicContractDetail = ({ contractId, onBack, hideMemberInfo = false }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const token = useSelector(state => state.user.user?.token);

  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const { data: contract } = useQuery({
    queryKey: ['contractDetail', contractId],
    queryFn: () => getContract(contractId, token),
    enabled: !!contractId && !!token,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['contractCategories'],
    queryFn: () => getContractCategories(token),
    enabled: !!token,
    staleTime: 1000 * 60 * 30,
  });

  // 백엔드를 통해 PDF blob을 가져와서 iframe에 표시
  useEffect(() => {
    if (!contractId || !token) return;

    let revoked = false;
    setPdfLoading(true);
    setPdfError(false);

    getContractPdf(contractId, token)
      .then(blob => {
        if (revoked) return;
        const url = window.URL.createObjectURL(blob);
        setPdfBlobUrl(url);
      })
      .catch(() => {
        if (!revoked) setPdfError(true);
      })
      .finally(() => {
        if (!revoked) setPdfLoading(false);
      });

    return () => {
      revoked = true;
      setPdfBlobUrl(prev => {
        if (prev) window.URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [contractId, token]);

  const handleDownloadPdf = async () => {
    try {
      const blob = await getContractPdf(contractId, token);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      const filename = contract?.name
        ? `${contract.name}_${contract.memberName || ''}.pdf`
        : `contract-${contractId}.pdf`;
      anchor.download = filename;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('PDF 다운로드에 실패했습니다.'));
    }
  };

  // 백엔드 프록시를 통해 영수증 다운로드
  const handleDownloadReceipt = async (receipt, filename) => {
    try {
      const res = await getReceiptDownload(receipt.id, token);
      const contentType = res.headers['content-type'] || '';
      const ext = contentType.includes('pdf') ? 'pdf' : contentType.split('/')[1]?.split(';')[0] || 'jpg';
      const url = window.URL.createObjectURL(res.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${filename}.${ext}`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('다운로드에 실패했습니다.'));
    }
  };

  // 실사 영수증 이미지들을 각각 다운로드
  const handleDownloadImages = async (receipts) => {
    for (let i = 0; i < receipts.length; i++) {
      await handleDownloadReceipt(receipts[i], `실사영수증_${i + 1}`);
    }
  };

  const handleMemberDetail = () => {
    const id = contract?.memberId;
    if (!id) return toast.error(t('회원 정보를 찾을 수 없습니다.'));
    navigate(`/member/${id}`);
  };

  if (!contract) return null;

  const registrationLabel = contract.registrationTypeLabel || contract.registrationType;
  const categoryLabel = categories.find(c => c.value === contract.category)?.label || contract.category;

  const pdfReceipts = contract.receipts?.filter(r => r.type === 'PDF') || [];
  const imageReceipts = contract.receipts?.filter(r => r.type === 'IMAGE') || [];

  return (
    <DetailContainer>
      {/* 상단 바 */}
      <MemberBar style={hideMemberInfo ? { background: 'none', border: 'none', padding: 0, justifyContent: 'flex-end' } : undefined}>
        {!hideMemberInfo && (
          <MemberInfoGroup>
            <MemberName>{contract.memberName}</MemberName>
            {contract.genderLabel && <MemberBadge>{contract.genderLabel}</MemberBadge>}
            <MemberDivider />
            <MemberText>{registrationLabel}</MemberText>
            <MemberDivider />
            <MemberText>{categoryLabel}</MemberText>
            <MemberDivider />
            <MemberText>{contract.phone}</MemberText>
            <MemberDetailButton onClick={handleMemberDetail}>{t('회원상세')}</MemberDetailButton>
          </MemberInfoGroup>
        )}

        <ActionButtonGroup>
          <PdfDownloadWrap>
            <PdfDownloadButton startIcon={<PictureAsPdfOutlinedIcon />}>
              {t('다운로드')}
            </PdfDownloadButton>
            <DownloadMenu className="download-menu">
              <DownloadMenuInner>
                <DownloadMenuItem
                  startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
                  onClick={handleDownloadPdf}>
                  전자계약서 다운로드
                </DownloadMenuItem>
                {pdfReceipts.length > 0 &&
                  pdfReceipts.map((receipt, idx) => (
                    <DownloadMenuItem
                      key={receipt.id}
                      startIcon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: '#EE735A' }} />}
                      onClick={() => handleDownloadReceipt(receipt, `영수증_${idx + 1}`)}>
                      영수증 다운로드{pdfReceipts.length > 1 ? ` (${idx + 1})` : ''}
                    </DownloadMenuItem>
                  ))}
                {imageReceipts.length > 0 && (
                  <DownloadMenuItem
                    startIcon={<ImageOutlinedIcon sx={{ fontSize: 18, color: '#0F7F85' }} />}
                    onClick={() => handleDownloadImages(imageReceipts)}>
                    실사 영수증 다운로드
                  </DownloadMenuItem>
                )}
              </DownloadMenuInner>
            </DownloadMenu>
          </PdfDownloadWrap>
          <BackToListButton onClick={onBack}>{t('목록으로')}</BackToListButton>
        </ActionButtonGroup>
      </MemberBar>

      {/* 본문: 좌측 계약서 PDF + 우측 영수증 */}
      <ContentRow>
        <LeftPanel>
          <ReceiptCard style={{ flex: 1 }}>
            <ReceiptTitle>{t('전자 계약서')}</ReceiptTitle>
            <ContractDocWrapper>
              {pdfLoading ? (
                <ReceiptEmpty style={{ minHeight: '400px' }}>
                  <CircularProgress sx={{ color: '#4AB3BC' }} />
                </ReceiptEmpty>
              ) : pdfBlobUrl ? (
                <iframe
                  src={`${pdfBlobUrl}#navpanes=0&scrollbar=1`}
                  title={contract.name}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <ReceiptEmpty style={{ minHeight: '400px' }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 48, color: '#D9D9D9' }} />
                  <ReceiptEmptyText>
                    {t(pdfError ? 'PDF를 불러올 수 없습니다.' : 'PDF가 아직 생성되지 않았습니다.')}
                  </ReceiptEmptyText>
                </ReceiptEmpty>
              )}
            </ContractDocWrapper>
          </ReceiptCard>
        </LeftPanel>

        <RightPanel>
          {(contract.category === 'REFUND' || contract.category === 'SUSPENSION') && (
            <ReceiptCard>
              <ReceiptTitle>{t(contract.category === 'REFUND' ? '환불 정보' : '중지 정보')}</ReceiptTitle>
              <SourceInfoRow>
                <SourceInfoLabel>{t('대상 상품')}</SourceInfoLabel>
                <SourceInfoValue>{contract.sourceProductName || '-'}</SourceInfoValue>
              </SourceInfoRow>
              {contract.category === 'REFUND' ? (
                <>
                  <SourceInfoRow>
                    <SourceInfoLabel>{t('환불계좌')}</SourceInfoLabel>
                    <SourceInfoValue>
                      {contract.refundBankName || contract.refundAccountNumber
                        ? `${contract.refundBankName || ''} ${contract.refundAccountNumber || ''}`.trim()
                        : '-'}
                    </SourceInfoValue>
                  </SourceInfoRow>
                  <SourceInfoRow>
                    <SourceInfoLabel>{t('예금주')}</SourceInfoLabel>
                    <SourceInfoValue>{contract.refundAccountHolder || '-'}</SourceInfoValue>
                  </SourceInfoRow>
                  <SourceInfoRow>
                    <SourceInfoLabel>{t('환불금액')}</SourceInfoLabel>
                    <SourceInfoValue>
                      {contract.refundAmount != null ? `${contract.refundAmount.toLocaleString()}${t('원')}` : '-'}
                    </SourceInfoValue>
                  </SourceInfoRow>
                  <SourceInfoRow>
                    <SourceInfoLabel>{t('환불사유')}</SourceInfoLabel>
                    <SourceInfoValue>{contract.refundReason || '-'}</SourceInfoValue>
                  </SourceInfoRow>
                </>
              ) : (
                <>
                  <SourceInfoRow>
                    <SourceInfoLabel>{t('중지기간')}</SourceInfoLabel>
                    <SourceInfoValue>
                      {contract.stopStartDate && contract.stopEndDate
                        ? `${contract.stopStartDate} ~ ${contract.stopEndDate}`
                        : '-'}
                    </SourceInfoValue>
                  </SourceInfoRow>
                  <SourceInfoRow>
                    <SourceInfoLabel>{t('중지사유')}</SourceInfoLabel>
                    <SourceInfoValue>{contract.stopReason || '-'}</SourceInfoValue>
                  </SourceInfoRow>
                </>
              )}
              <SourceInfoRow>
                <SourceInfoLabel>{t('처리상태')}</SourceInfoLabel>
                <SourceInfoValue>
                  {contract.processedDate ? `${t('처리완료')} (${contract.processedDate})` : t('처리대기')}
                </SourceInfoValue>
              </SourceInfoRow>
            </ReceiptCard>
          )}

          <ReceiptCard>
            <ReceiptTitle>{t('전자 영수증')}</ReceiptTitle>
            {pdfReceipts.length > 0 ? (
              pdfReceipts.map(receipt => (
                <ReceiptPdfViewer key={receipt.id}>
                  <iframe
                    src={`${receipt.url}#navpanes=0&scrollbar=1`}
                    title="전자 영수증"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                </ReceiptPdfViewer>
              ))
            ) : (
              <ReceiptEmpty>
                <DescriptionOutlinedIcon sx={{ fontSize: 40, color: '#D9D9D9' }} />
                <ReceiptEmptyText>{t('등록된 영수증이 없습니다.')}</ReceiptEmptyText>
              </ReceiptEmpty>
            )}
          </ReceiptCard>

          <ReceiptCard>
            <ReceiptTitle>{t('실사 영수증')}</ReceiptTitle>
            {imageReceipts.length > 0 ? (
              imageReceipts.map(receipt => (
                <ReceiptImage
                  key={receipt.id}
                  src={receipt.url}
                  alt="실사 영수증"
                  onClick={() => setPreviewImage(receipt.url)}
                />
              ))
            ) : (
              <ReceiptEmpty>
                <ImageOutlinedIcon sx={{ fontSize: 40, color: '#D9D9D9' }} />
                <ReceiptEmptyText>{t('등록된 영수증이 없습니다.')}</ReceiptEmptyText>
              </ReceiptEmpty>
            )}
          </ReceiptCard>
        </RightPanel>
      </ContentRow>

      {/* 실사 영수증 이미지 프리뷰 */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'pointer',
          }}>
          <img
            src={previewImage}
            alt="실사 영수증"
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px', objectFit: 'contain' }}
          />
        </div>
      )}
    </DetailContainer>
  );
};

export default ElectronicContractDetail;
